import { useEffect, useRef } from 'react';
import { useHilReader, useHilQueue } from '@bizfirst/hil-react';
import { FlowFooterEngine, FlowObserverPanelEngine } from '@flow-studio/flow-observer-core';
import { useUIStore } from '@flow-studio/store';

/**
 * Bridges the real @bizfirst/hil-react store (the one that actually drives HIL
 * form/chat sessions — see WorkflowApp.tsx / HilObservePanel.tsx) into the
 * FlowObserver dock:
 *  - keeps the 'hil-pending' footer badge count/visibility in sync
 *  - auto-activates the 'hil-actions' dock tab whenever a brand-new session
 *    arrives, the same way TopToolbar's Observe button reopens the panel
 *
 * Must be mounted for the lifetime of the execution session (WorkflowEditor).
 * The badge itself is registered in FlowObserverInit — this hook only drives
 * its state and the tab's auto-open behavior.
 */
export function useHilPanelBridge(): void {
  const reader = useHilReader();
  const { active, count } = useHilQueue();
  const { hilFormsVisible, hilChatVisible } = useUIStore();
  const lastSessionIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (count > 0) {
      FlowFooterEngine.updateBadge('hil-pending', { visible: true, value: count, state: 'active' });
    } else {
      FlowFooterEngine.updateBadge('hil-pending', { visible: false, state: 'idle' });
    }
  }, [count]);

  useEffect(() => {
    const sessionId = active?.Session.EngageSessionID ?? null;
    const isNewSession = sessionId !== null && sessionId !== lastSessionIdRef.current;
    lastSessionIdRef.current = sessionId;

    if (!isNewSession) return;

    const isChat = reader?.hilType().toLowerCase() === 'chat';
    const shouldAutoOpen = isChat ? hilChatVisible : hilFormsVisible;
    if (shouldAutoOpen) {
      FlowObserverPanelEngine.activateTab('hil-actions');
    }
  }, [active, reader, hilFormsVisible, hilChatVisible]);
}
